import React, {useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {take} from 'lodash'
import {InputNumber, message, Image as AntdImage} from 'antd'
import ReactImageMagnify from 'react-image-magnify'

import './index.less'
import './mobile.less'
import Layout from '@/components/Layout'
import ProductItem from '@/components/ProductItem'
import OSwiper from 'src/components/OSwiper'
import noImage from '@/assets/img/icon/noImage.png'
import favoriteActiveIcon from '@/assets/img/icon/favoriteActive.png'
import favoriteDefaultIcon from '@/assets/img/icon/favoriteDefault.png'
import {
  addProductToFavoriteList,
  getMoreProducts,
  getProductDetail,
  removeProductFromFavoriteList,
  getTopColors,
} from '@/api/req-api'
import {addProductToCart, removeCartItem, updateCartItemQuantity} from '@/store/modules/cart.slice'
import {
  checkLogin, getFormatMoney, getFormatProductListByCartList, getProductItemBtnType} from '@/common'
import SkuListView from './skulist'

export default function ProductDetail() {
  const {id} = useParams();
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const {t} = useTranslation()

  const commonState = useSelector(state => state.common)
  const cartState = useSelector(state => state.cart)
  const {productCategoryMap, isLogged, isInternal, isSCM} = commonState
  const rootClassName = commonState.isMobile ? 'product-detail-mobile-container' : 'product-detail-container'
  const {orderCartProductList} = cartState

  const [nowImg, setNowImg] = useState('')
  const [widthData, setWidthData] = useState(window.innerWidth)
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 })
  const [productDetail, setProductDetail] = useState({})
  const [moreProducts, setMoreProducts] = useState([])
  const [topColors, setTopColors] = useState([])
  const [quantity, setQuantity] = useState(1);
  const [productList, setProductList] = useState([])
  const [currentMedia, setCurrentMedia] = useState(null)

  const useMoreProducts = useMemo(() => {
    return getFormatProductListByCartList(moreProducts, orderCartProductList)
  }, [moreProducts, orderCartProductList])

  const useProductList = useMemo(() => {
    return getFormatProductListByCartList(productList, orderCartProductList)
  }, [productList, orderCartProductList])

  // normalize images + videos into one array
  const mediaList = useMemo(() => {
    const imgs = productDetail?.Images || [];
    const vids = (productDetail?.videos || []).map(v => v.video_file);
    return [...imgs, ...vids]; // images first, then videos
  }, [productDetail])

  useEffect(() => {
    if (!nowImg) return

    const image = new Image();
    image.src = nowImg;
    image.onload = () => {
      setImgSize({ width: image.width, height: image.height });
    };
  }, [nowImg]);

  useEffect(() => {
    if (mediaList.length > 0) {
      setCurrentMedia(mediaList[0]);
    }
  }, [mediaList])

  // track whether current main media is image or video
  const isVideo = useMemo(() => {
    if (!currentMedia || typeof currentMedia !== 'string') return false;
    const m = currentMedia.toLowerCase();
    return m.includes('.mp4') || m.includes('.mov') || m.includes('/video/');
  }, [currentMedia])

  const fetchDetail = async (id) => {
    try {
      const {data: [productDetailResponse]} = await getProductDetail(id)
      setProductDetail(productDetailResponse)
      // setNowImg(productDetailResponse?.Images?.[0])
      setProductList(productDetailResponse.related_products || [])
      const {data: moreProductResponse} = await getMoreProducts(productDetailResponse.ProductGroup_id)
      setMoreProducts(take(moreProductResponse, 8))
      const {data: topColorsResponse} = await getTopColors()
      setTopColors(topColorsResponse.top)
    } catch (e) {
      //todo show error 
    }
  }
  const initPage = () => {
    if (id) {
      fetchDetail(id)
    }
  }
  const getPageWidth = ()=>{
    var getWidth = window.innerWidth;
    setWidthData(getWidth)
  }
  window.onresize = ()=>{
    getPageWidth()
  }
  useEffect(() => {
    initPage()
  }, [id])

  const renderProductImgSwiperSlideContent = (item, index) => {
    const isVideoItem = item.toLowerCase().includes('.mp4') || item.toLowerCase().includes('.mov');
    return <div
      className={'product-detail-top-info-left-img-container'}
      key={`product_detail_img_${index}`}
      onClick={(e)=>{setCurrentMedia(item)}}
    >
      {isVideoItem ? (
        <video
          src={item}
          muted
          style={{
            width: "100%",
            borderRadius: 6,
            cursor: "pointer"
          }}
        />
      ) : (
        <img
          src={item}
          className={item === currentMedia ? 'detail-swiper-img detail-swiper-img-special' : 'detail-swiper-img'}
        />
      )}
    </div>
  }

  const renderProductDetailImg = () => {
    const smallWidth = Math.min(imgSize.width, 400);
    const smallHeight = imgSize.height * (smallWidth / imgSize.width || 1);
    return (
      <div className={'product-detail-top-info-left-img-container-top'}>
        {isVideo ? (
          <video
            src={currentMedia}
            controls
            style={{ width: "100%", borderRadius: 6 }}
          />
        ) : (
          <>
          {!commonState.isMobile ? (
            <ReactImageMagnify {...{
              smallImage: {
                alt: '',
                isFluidWidth: true,
                src: currentMedia || noImage,
                width: smallWidth,
                height: smallHeight,
              },
              largeImage: {
                src: currentMedia || noImage,
                width: smallWidth * 2,
                height: smallHeight * 2,
              },
              enlargedImageContainerDimensions: {
                width: '150%',
                height: '150%',
              },
              enlargedImageContainerStyle:{
                zIndex: 9999
              }
            }} /> 
          ) : (
            <AntdImage src={currentMedia || noImage} />
          )}
        </>
      )}
    </div>
  )}

  const renderProductImgSwiper = () => <OSwiper
    className={'product-detail-top-info-left-product-img-swiper-container'}
    swiperClassName={'product-detail-top-info-left-product-img-swiper-swiper'}
    prevClassName={'product-detail-top-info-left-product-img-swiper-prev'}
    nextClassName={'product-detail-top-info-left-product-img-swiper-next'}
    data={mediaList}
    // data={productDetail?.Images && productDetail.Images.length > 0 ? productDetail.Images : [noImage]}
    renderSwiperSlideContent={renderProductImgSwiperSlideContent}
    // isShowPagination={true}
    // isShowNavigation={false}
    isShowPagination={false}
    isShowNavigation={true}
    slidesPerView={commonState.isMobile ? 2 : Math.floor(window.innerWidth/350)}
  />

  const renderSwiperSlideContent = (item, index) => {
    return <ProductItem
      img={item.Thumbnail}
      price={item.Price}
      name={item.Name}
      description={item.Description}
      isShowPrice={false}
      isFormatPriceToMoney={true}
      onBtnClick={() => handleBeforeAddToCart(item, 1)}
      onProductItemClick={() => handleProductClick(item)}
      isFavorited={item.IsFavorite}
      isShowFavorite={true}
      onFavoriteIconClick={() => handleFavoriteIconClick(item)}
      quantity={item.Quantity}
      btnType={getProductItemBtnType(item.Quantity)}
      onMinBtnClick={() => handleMinBtnClick(item, item.Quantity)}
      onPlusBtnClick={() => handleAddToCart(item)}
      isPromotion={item.IsPromotion}
      stock={item.ReadyQuantity}
      inventoryQuantity={item.ReadyQuantity}
      minQty={item.MinQuantity}
      isOverSaleable={item.IsOverSaleable}
      discountDescription={item.DiscountDescription}
      originalPrice={item.OriginalPrice}
    />
  }
  const renderProductList = () => <OSwiper
    className={'product-detail-top-product-list-container'}
    data={useProductList}
    renderSwiperSlideContent={renderSwiperSlideContent}
    slidesPerView={commonState.isMobile ? 2 : 4}
  />

  const handleMinBtnClick = (item, qty) => {
    const newQty = qty - 1
    if (newQty < 1) {
      return dispatch(removeCartItem({item}))
    }
    dispatch(updateCartItemQuantity({item, quantity: newQty}))
  }
  const handleAddToCart = (item, qty) => {
    dispatch(addProductToCart({item, quantity: qty}))
  }
  const handleBeforeAddToCart = (item, qty) => {
    checkLogin(
      navigate,
      commonState.isLogged,
      location,
      () => handleAddToCart(item, qty)
    )
  }

  const handleChangeFavoriteStatus = (item) => {
    const isFavorite = item.IsFavorite
    const msg = isFavorite ? t('Cancel favorite successfully') : t('Add favorite successfully')
    const fun = isFavorite ? removeProductFromFavoriteList : addProductToFavoriteList
    fun(item).then(() => {
      message.success(msg)
      initPage()
    })
  }
  const handleBeforeChangeFavoriteStatus = (item, callback) => {
    checkLogin(
      navigate,
      isLogged,
      location,
      callback
    )
  }
  const handleFavoriteIconClick = (item, isProductDetail = false) => {
    handleBeforeChangeFavoriteStatus(item, () => handleChangeFavoriteStatus(item, isProductDetail))
  }
  
  const handleProductClick = item => {
    const path = `/productDetail/${item.ProductNum}`
    // if (location.pathname === path) {
    //   return
    // }
    navigate(path);
  }

  const renderProductGroupItem = (item, index) => <div
    className={'product-detail-more-product-item-container'}
    key={`product_group_2_item_${index}`}>
    <ProductItem
      img={item.Thumbnail} 
      price={item.Price} 
      name={item.Name}
      description={item.Description}
      isShowPrice={false}
      isFormatPriceToMoney={true}
      onBtnClick={() => handleBeforeAddToCart(item, 1)}
      onProductItemClick={() => handleProductClick(item)}
      isFavorited={item.IsFavorite}
      isShowFavorite={false}
      onFavoriteIconClick={() => handleFavoriteIconClick(item)}
      quantity={item.Quantity}
      btnType={getProductItemBtnType(item.Quantity)}
      onMinBtnClick={() => handleMinBtnClick(item, item.Quantity)}
      onPlusBtnClick={() => handleAddToCart(item)}
      isPromotion={item.IsPromotion}
      stock={item.ReadyQuantity}
      inventoryQuantity={item.ReadyQuantity }
      minQty={item.MinQuantity}
      isOverSaleable={item.IsOverSaleable}
      discountDescription={item.DiscountDescription}
      originalPrice={item.OriginalPrice}
    />
  </div>

  const renderTopColorsItem = (item, index) => <div
    className={'product-detail-top-colors-item-container'}
    key={`product_group_2_item_${index}`}>
    <div>
      <div className={'product-top-colors-item-container'}>
        {item.Image ? <img src={item.Image} alt=""/> : <img src={noImage} alt=""/>}
      </div>
      <div className={'product-item-des overflow-ellipsis-2'}>
        <span>{item.Color}</span>
      </div>
    </div>
  </div>

  const renderRichTextDescription = () => {
    if (!!productDetail?.rich_text_description) {
      return <div className={'product-detail-rich-text-description-container'}>
        <div className={'product-detail-rich-text-description-title'}>{t('Description')}</div>
        <div className="product-detail-rich-text-description-content" dangerouslySetInnerHTML={{__html: productDetail?.rich_text_description}}/>
      </div>
    }
  }
  const renderMoreProduct = () => <div className={'product-detail-more-product-container'}>
    <div className={'product-detail-more-product-title'}>{t('MORE PRODUCT')}</div>
    <div className={'product-detail-more-product-list'}>
      {useMoreProducts.map((item, index) => renderProductGroupItem(item, index))}
    </div>
  </div>

  const renderTopColor = () => <div className={'product-detail-more-product-container'}>
    <div className={'product-detail-more-product-title'}>{t('Top Selling Colors')}</div>
    <div className={'product-top-color-list'}>
      {topColors.map((item, index) => renderTopColorsItem(item, index))}
    </div>
  </div>
  const renderProductInfo = () => {
    const maxQuantity = productDetail?.ProductCategory?.MaxQuantity ?? productDetail?.MaxQuantity
    return <div className={'product-detail-top-info-right-container'}>
      {/*<div className={'product-detail-top-info-right-product-description-container'}>
        <div className={'product-detail-top-info-right-product-description-item'}>
        // <div className={'product-detail-top-info-right-product-description-title'}>
          {productDetail?.ShortItemName}
          // {productDetail?.Description}
        </div>
      </div> */}
    {productDetail?.ShortColorCode && (
      <div className={'product-detail-top-info-right-product-description-container'}>
        <div className="product-detail-top-info-right-product-description-title">
          {productDetail.ShortColorCode}
        </div>
      </div>
    )}
    {productDetail?.FrontPOColor && (
      <div className="product-detail-top-info-right-product-style-container">
        <div className="product-detail-top-info-right-product-style-title">
          <div className="product-detail-top-info-right-product-style-item">
            {productDetail.FrontPOColor}
          </div>
        </div>
      </div>
    )}
    {hasPromotion && <div className={'product-detail-top-info-right-product-promotion-container'}>
      <div className={'product-detail-top-info-right-product-promotion-title'}>
        {t('Promotion')}
      </div>
      <div className={'product-detail-top-info-right-product-promotion-item'}>
        {productDetail?.DiscountDescription.map(description => <div>
          {description}
        </div>)}
      </div>
    </div>}
    {productDetail?.group_products && (
      <div className={'product-detail-top-info-right-product-style-container'}>
        <div className={'product-detail-top-info-right-product-style-box'}>
          <div className={'product-detail-top-info-right-product-style-title'}>
            {t('Size Options')}
          </div>
        </div>
        <SkuListView data={productDetail} changeSku={(detail)=>{setProductDetail(detail)}}/>
      </div>
    )}
    {productDetail?.PackageSize && (
      <div className={'product-detail-top-info-right-product-style-container'}>
        <div className={'product-detail-top-info-right-product-style-box'}>
          <div className={'product-detail-top-info-right-product-style-title'}>
            {t('Pkg Size')}
          </div>
        </div>
        <div className={'product-detail-top-info-right-product-style-box'}>
          <div className={'product-detail-top-info-right-product-style-item'}>
            {productDetail.PackageSize}
          </div>
        </div>
      </div>
    )}
    {productDetail?.Color &&
      <div className={'product-detail-top-info-right-product-style-container'}>
        <div className={'product-detail-top-info-right-product-style-box'}>
          <div className={'product-detail-top-info-right-product-style-title'}>
            {t('Color')}
          </div>
        </div>
        <div className={'product-detail-top-info-right-product-style-box'}>
          <div className={'product-detail-top-info-right-product-style-item'}>
            {productDetail.Color}
          </div>
        </div>
      </div>
    }
    <div className={'product-detail-top-info-right-product-style-container'}>
      <div className={'product-detail-top-info-right-product-style-box'}>
        <div className={'product-detail-top-info-right-product-style-title'}>
          {t('Ready')}
        </div>
      </div>
      <div className={'product-detail-top-info-right-product-style-box'}>
        <div className={'product-detail-top-info-right-product-style-item'}>
          {/* {productDetail.InventoryQuantity} */}
          {(productDetail?.ReadyQuantity > maxQuantity) ? 
            <div className={'cart-order-summary-product-list-item-right-info-price'}>
              Available
            </div> 
            :
            <div className={'cart-order-summary-product-list-item-right-info-price'}>
              {productDetail?.ReadyQuantity}
            </div> 
          }
        </div>
      </div>
    </div>
    <div className={'product-detail-top-info-right-product-style-container'}>
      <div className={'product-detail-top-info-right-product-style-box'}>
        <div className={'product-detail-top-info-right-product-style-title'}>
          {t('Pending')}
        </div>
      </div>
      <div className={'product-detail-top-info-right-product-style-box'}>
        <div className={'product-detail-top-info-right-product-style-item'}>
          <div className={'cart-order-summary-product-list-item-right-info-price'}>
            {productDetail?.PendingQuantity}
          </div> 
        </div>
      </div>
    </div>
    {
      false && <div className={'product-detail-top-info-right-price'}>
        <span className={'price'}>
          {`$${getFormatMoney(productDetail.Price)}`}
        </span>
      </div>
    }
    {/* add to cart */}
    {
      !isSCM &&
      <div className={'product-detail-top-info-right-bottom-container'}>
        <div className={'product-detail-top-info-right-bottom-left-container'}>
          <div className={'product-detail-top-info-right-bottom-left-label'}>{t('QTY')}</div>
          <InputNumber
            className={'product-detail-top-info-right-bottom-left-input'}
            min={1}
            max={productDetail?.IsOverSaleable ? undefined : productDetail?.ReadyQuantity}
            defaultValue={quantity}
            onChange={setQuantity}
            disabled={isOutOfStock || isUnderMin}
          />
          <div className={'product-detail-top-info-right-bottom-left-label'}>{productDetail?.PackageType}</div>
        </div>
        {
          isOutOfStock ? (
            <div className="product-detail-top-info-right-bottom-btn-disabled">
              {t('Coming soon')}
            </div>
          ) : isUnderMin ? (
            <div className="product-detail-top-info-right-bottom-btn-disabled">
              {t('Add to cart')}
            </div>
          ) : (
            <div
              className="product-detail-top-info-right-bottom-btn"
              onClick={() => handleBeforeAddToCart(productDetail, quantity)}
            >
              {t('Add to cart')}
            </div>
          )
        }
      </div>
    }
  </div>
  }

  const isOutOfStock = productDetail?.IsOverSaleable ? false : productDetail?.ReadyQuantity <= 0
  const isUnderMin = productDetail?.IsOverSaleable ? false : productDetail?.ReadyQuantity < productDetail?.ProductCategory?.MinQuantity
  const hasPromotion = productDetail?.DiscountDescription && productDetail?.DiscountDescription?.length > 0;
  return (
    <Layout
      isDetailPage={false}
      detailPageTitle={productDetail?.Name}>
      <div className={rootClassName}>
        <div className={'product-detail-top-container'}>
          <div className={'product-detail-top-title'}>
            <span>{productDetail?.ProductCategory1_id ? productCategoryMap[productDetail.ProductCategory1_id]?.Name : ''}</span>
            {/*<div>{productDetail?.Name}</div>*/}
          </div>
          <div className={'product-detail-top-info-container'}>
            <div className={'product-detail-top-info-left-container'}>
              {renderProductDetailImg()}
              {renderProductImgSwiper()} 
            </div>
            {/* {widthData > 1300 ? renderProductInfo() : null} */}
            {renderProductInfo()}
          </div>
        </div>
        {/*{renderRichTextDescription()}*/}
        {/*{renderMoreProduct()}*/}
        {renderTopColor()}
      </div>
    </Layout>
  )
}
